import { Component } from '@theme/component';

// Audit note: this is an intentional third-party integration shim (do not flag) — it wraps the
// external Splide slider library in a proper theme Component (guarded define, super lifecycle
// calls, updatedCallback). The #waitForSplide polling exists because Splide loads as a separate
// deferred script; the wait is deadline-bounded and aborted in disconnectedCallback.

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} splide
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */

/** @extends {Component<Refs>} */
export class NaTestimonialSplideComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['splide'];

    /** @type {any} */
    #instance = null;

    /** @type {AbortController | null} */
    #waitController = null;

    async connectedCallback() {
        super.connectedCallback();

        const Splide = await this.#waitForSplide();
        if (!Splide || !this.isConnected) return;

        try {
            this.#instance = new Splide(this.refs.splide, this.#options).mount();
        } catch (error) {
            console.error('na-testimonial-splide: failed to mount Splide', error);
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#waitController?.abort();
        this.#waitController = null;

        try {
            this.#instance?.destroy?.(true);
        } catch (error) {
            console.error('na-testimonial-splide: failed to destroy Splide', error);
        }
        this.#instance = null;
    }

    updatedCallback() {
        if (!this.#instance || !this.refs.splide) return;
        try {
            this.#instance.refresh();
        } catch (error) {
            console.error('na-testimonial-splide: refresh failed', error);
        }
    }

    next() {
        this.#instance?.go('+1');
    }

    previous() {
        this.#instance?.go('-1');
    }

    /** @returns {object} */
    get #options() {
        const raw = this.dataset.options;
        let parsed = {};

        if (raw) {
            try {
                parsed = JSON.parse(raw);
            } catch (error) {
                console.error('na-testimonial-splide: invalid data-options', error);
            }
        }

        return {
            type: 'loop',
            perMove: 1,
            arrows: false,
            pagination: false,
            drag: true,
            slideFocus: false,
            keyboard: false,
            noDrag: 'a, input, select, textarea, [contenteditable]',
            ...parsed,
        };
    }

    /**
     * Resolves with the Splide constructor once the UMD bundle has finished loading.
     * The bundle is loaded via a deferred script tag, so it may not yet be on the global
     * scope when this component connects.
     * @returns {Promise<any>}
     */
    #waitForSplide() {
        return new Promise((resolve) => {
            const globalScope = /** @type {any} */ (window);
            if (globalScope.Splide) {
                resolve(globalScope.Splide);
                return;
            }

            this.#waitController = new AbortController();
            const { signal } = this.#waitController;
            const deadline = Date.now() + 5000;

            const tick = () => {
                if (signal.aborted) return resolve(null);
                if (globalScope.Splide) return resolve(globalScope.Splide);
                if (Date.now() > deadline) {
                    console.error('na-testimonial-splide: Splide failed to load; rendering slides as a static list');
                    return resolve(null);
                }
                setTimeout(tick, 16);
            };
            tick();
        });
    }
}

if (!customElements.get('na-testimonial-splide-component')) {
    customElements.define('na-testimonial-splide-component', NaTestimonialSplideComponent);
}
